import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Mail, Paperclip, Send, ArrowLeft, X, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SendEmail = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailData, setEmailData] = useState({
    recipientEmail: '',
    subject: '',
    message: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [emailSent, setEmailSent] = useState(false);

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('customerAdminToken');
    if (!token) {
      navigate('/customer-admin/login');
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEmailData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
    toast.success(`${files.length} file(s) attached`);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    toast.info('Attachment removed');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();

    // Validation
    if (!emailData.recipientEmail) {
      toast.error('Please enter recipient email address');
      return;
    }

    if (!emailData.recipientEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!emailData.subject) {
      toast.error('Please enter email subject');
      return;
    }

    if (!emailData.message) {
      toast.error('Please enter email message');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('customerAdminToken');
      
      // Create FormData to handle file uploads
      const formData = new FormData();
      formData.append('recipient_email', emailData.recipientEmail);
      formData.append('subject', emailData.subject);
      formData.append('message', emailData.message);
      
      // Append all attachments
      attachments.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send email');
      }

      const result = await response.json();
      
      setEmailSent(true);
      toast.success('Email sent successfully!');
      
      // Reset form after 2 seconds
      setTimeout(() => {
        setEmailData({
          recipientEmail: '',
          subject: '',
          message: ''
        });
        setAttachments([]);
        setEmailSent(false);
      }, 3000);

    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.message || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/customer-admin')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                <Mail className="w-8 h-8 text-red-600" />
                Send Email
              </h1>
              <p className="text-gray-600 mt-1">Send custom emails to customers with attachments</p>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {emailSent && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-green-800">Email Sent Successfully!</h3>
              <p className="text-green-700 text-sm">Your email has been sent to {emailData.recipientEmail}</p>
            </div>
          </div>
        )}

        {/* Email Form */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-red-600 to-orange-500 text-white">
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Compose Email
            </CardTitle>
            <CardDescription className="text-gray-100">
              From: Admin@netixfibernet.com
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSendEmail} className="space-y-6">
              {/* Recipient Email */}
              <div>
                <Label htmlFor="recipientEmail" className="text-base font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4 text-red-600" />
                  Recipient Email Address
                </Label>
                <Input
                  id="recipientEmail"
                  name="recipientEmail"
                  type="email"
                  value={emailData.recipientEmail}
                  onChange={handleInputChange}
                  placeholder="customer@example.com"
                  className="mt-2 text-base"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">Enter the recipient's email address</p>
              </div>

              {/* Subject */}
              <div>
                <Label htmlFor="subject" className="text-base font-semibold">
                  Email Subject
                </Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  value={emailData.subject}
                  onChange={handleInputChange}
                  placeholder="Enter email subject"
                  className="mt-2 text-base"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-base font-semibold">
                  Email Content
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={emailData.message}
                  onChange={handleInputChange}
                  placeholder="Type your message here..."
                  rows={12}
                  className="mt-2 text-base resize-none"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Write your message. It will be displayed in a professional template with Netix Fibernet logo.
                </p>
              </div>

              {/* File Attachments */}
              <div>
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-red-600" />
                  Attachments (Optional)
                </Label>
                
                <div className="mt-2">
                  <input
                    type="file"
                    id="file-upload"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('file-upload').click()}
                    className="flex items-center gap-2"
                  >
                    <Paperclip className="w-4 h-4" />
                    Add Attachment
                  </Button>
                </div>

                {/* Display selected files */}
                {attachments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      Selected Files ({attachments.length}):
                    </p>
                    {attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <Paperclip className="w-4 h-4 text-gray-500" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttachment(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Email Template Information:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Your email will be sent from <strong>Admin@netixfibernet.com</strong></li>
                      <li>Netix Fibernet logo and branding will be included automatically</li>
                      <li>Professional footer with company contact information</li>
                      <li>All attachments will be included with the email</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Send Button */}
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/customer-admin')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Email
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Email History Preview */}
        <div className="mt-6 bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Email Preview</h3>
          <p className="text-sm text-gray-600">
            Your email will be formatted with Netix Fibernet's professional template including:
          </p>
          <ul className="mt-2 space-y-1 text-sm text-gray-700">
            <li>✓ Netix Fibernet logo header with gradient background</li>
            <li>✓ Your custom message in a formatted message box</li>
            <li>✓ Professional footer with company contact details</li>
            <li>✓ All file attachments will be included</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SendEmail;
